(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const setOpen = (element, open) => {
    if (!element) return;
    element.classList.toggle('is-open', open);
    element.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.classList.toggle('omc-drawer-open', open);
  };

  function initHeader() {
    const header = qs('[data-site-header]');
    if (!header || header.dataset.ready) return;
    header.dataset.ready = 'true';
    const menu = qs('[data-mobile-menu]');
    qsa('[data-menu-open]').forEach((button) => button.addEventListener('click', () => setOpen(menu, true)));
    qsa('[data-menu-close]').forEach((button) => button.addEventListener('click', () => setOpen(menu, false)));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setOpen(menu, false);
        setOpen(qs('[data-cart-drawer]'), false);
        setOpen(qs('[data-search-drawer]'), false);
        setOpen(qs('[data-size-guide]'), false);
      }
    });
  }

  function initDrawers() {
    const cartDrawer = qs('[data-cart-drawer]');
    const searchDrawer = qs('[data-search-drawer]');
    qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', () => {
      refreshCartDrawer().finally(() => setOpen(cartDrawer, true));
    }));
    qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', () => setOpen(cartDrawer, false)));
    qsa('[data-search-open]').forEach((button) => button.addEventListener('click', () => setOpen(searchDrawer, true)));
    qsa('[data-search-close]').forEach((button) => button.addEventListener('click', () => setOpen(searchDrawer, false)));
    qsa('[data-size-guide-open]').forEach((button) => button.addEventListener('click', () => setOpen(qs('[data-size-guide]'), true)));
    qsa('[data-size-guide-close]').forEach((button) => button.addEventListener('click', () => setOpen(qs('[data-size-guide]'), false)));
  }

  async function refreshCartDrawer() {
    const body = qs('[data-cart-drawer-body]');
    if (!body) return;
    const response = await fetch(`${window.location.pathname}?sections=cart-drawer`);
    const json = await response.json();
    const html = json['cart-drawer'];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = qs('[data-cart-drawer-body]', doc);
    if (next) body.innerHTML = next.innerHTML;
    bindCartControls(body);
    updateCartCount();
  }

  async function updateCartCount() {
    try {
      const cart = await fetch('/cart.js').then((res) => res.json());
      qsa('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
    } catch (error) {}
  }

  let cartChangeQueue = Promise.resolve();

  async function refreshCartPage() {
    const page = qs('.omc-cart-page');
    if (!page) return;

    const note = qs('[name="note"]', page)?.value;
    const response = await fetch(
      window.location.pathname + window.location.search,
      { headers: { 'Accept': 'text/html' }, cache: 'no-store' }
    );

    if (!response.ok) throw new Error('Could not refresh the cart page.');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextPage = qs('.omc-cart-page', doc);

    if (nextPage) {
      page.innerHTML = nextPage.innerHTML;

      const nextNote = qs('[name="note"]', page);
      if (nextNote && note !== undefined) nextNote.value = note;

      bindCartControls(page);
    }
  }

  async function changeCartLine(key, adjustment, remove = false) {
    const cartResponse = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      cache: 'no-store'
    });

    if (!cartResponse.ok) throw new Error('Could not read the cart.');

    const currentCart = await cartResponse.json();
    const item = currentCart.items.find((line) => line.key === key);

    if (!item) return;

    const quantity = remove
      ? 0
      : Math.max(0, item.quantity + adjustment);

    const changeResponse = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: key, quantity })
    });

    if (!changeResponse.ok) throw new Error('Could not update the cart.');

    const updatedCart = await changeResponse.json();

    qsa('[data-cart-count]').forEach((node) => {
      node.textContent = updatedCart.item_count;
    });

    await Promise.all([
      refreshCartPage(),
      refreshCartDrawer()
    ]);
  }

  function queueCartChange(key, adjustment, remove = false) {
    cartChangeQueue = cartChangeQueue
      .then(() => changeCartLine(key, adjustment, remove))
      .catch((error) => {
        console.error('Cart update failed:', error);
      });
  }

  function bindCartControls(root = document) {
    qsa('[data-cart-qty]', root).forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';

      button.addEventListener('click', () => {
        const line = button.closest('[data-cart-line]');
        const displayedQuantity = Number(qs('.omc-qty input', line)?.value);
        const buttonQuantity = Number(button.dataset.quantity);
        const adjustment = buttonQuantity > displayedQuantity ? 1 : -1;

        queueCartChange(button.dataset.cartQty, adjustment);
      });
    });

    qsa('[data-cart-remove]', root).forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';

      button.addEventListener('click', () => {
        queueCartChange(button.dataset.cartRemove, 0, true);
      });
    });
  }

  function initProductForms(root = document) {
    qsa('[data-product-root]', root).forEach((productRoot) => {
      if (productRoot.dataset.ready) return;
      productRoot.dataset.ready = 'true';
      let variants = [];
      try { variants = JSON.parse(productRoot.dataset.productJson); } catch (error) {}
      const form = qs('[data-product-form]', productRoot);
      const idInput = qs('[data-variant-id]', productRoot);
      const price = qs('[data-product-price]', productRoot);
      const addText = qs('[data-add-text]', productRoot);
      const submit = qs('.omc-product-form__submit', productRoot);
      const sku = qs('[data-product-sku]', productRoot);
      const optionInputs = qsa('input[name^="options["], select[name^="options["]', productRoot);
      const moneyFormat = window.OMC_MONEY_FORMAT || '${{amount}}';
      const formatMoney = (cents) => {
        const value = (Number(cents) / 100).toFixed(2);
        return moneyFormat.replace('{{amount}}', value).replace('{{ amount }}', value);
      };
      const selectedOptions = () => {
        const grouped = {};
        optionInputs.forEach((input) => {
          if (input.tagName === 'SELECT' || input.checked) grouped[input.name] = input.value;
        });
        return Object.values(grouped);
      };
      const findVariant = () => variants.find((variant) => variant.options.every((option, index) => option === selectedOptions()[index]));
      const update = () => {
        const variant = findVariant();
        if (!variant) return;
        idInput.value = variant.id;
        if (price) price.innerHTML = `<span class="omc-price">${formatMoney(variant.price)}</span>`;
        if (sku) sku.textContent = variant.sku || '';
        if (submit) submit.disabled = !variant.available;
        if (addText) addText.textContent = variant.available ? 'Add to bag' : 'Sold out';
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
        if (variant.featured_media) {
          const media = qs(`[data-media-id="${variant.featured_media.id}"]`, productRoot);
          media?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        }
      };
      optionInputs.forEach((input) => input.addEventListener('change', update));
      qsa('[data-share-product]', productRoot).forEach((button) => {
        button.addEventListener('click', async () => {
          const payload = { title: document.title, url: window.location.href };
          try {
            if (navigator.share) await navigator.share(payload);
            else if (navigator.clipboard) await navigator.clipboard.writeText(payload.url);
            button.textContent = 'Copied';
            setTimeout(() => { button.textContent = button.dataset.originalText || 'Copy product link'; }, 1400);
          } catch (error) {}
        });
        button.dataset.originalText = button.textContent;
      });
      form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        submit?.classList.add('is-loading');
        const message = qs('[data-product-message]', productRoot);
        try {
          const response = await fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: new FormData(form) });
          if (!response.ok) throw new Error('Could not add to cart.');
          if (message) message.textContent = 'Added to bag.';
          await refreshCartDrawer();
          setOpen(qs('[data-cart-drawer]'), true);
        } catch (error) {
          if (message) message.textContent = error.message || 'Something went wrong.';
        } finally {
          submit?.classList.remove('is-loading');
        }
      });
    });
  }

  function initPredictiveSearch() {
    const input = qs('[data-predictive-input]');
    const results = qs('[data-predictive-results]');
    if (!input || !results || input.dataset.ready) return;
    input.dataset.ready = 'true';
    let controller;
    input.addEventListener('input', async () => {
      const term = input.value.trim();
      if (controller) controller.abort();
      if (term.length < 2) { results.innerHTML = ''; return; }
      controller = new AbortController();
      try {
        const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,page,article&resources[limit]=6`, { signal: controller.signal });
        const data = await response.json();
        const products = data.resources.results.products || [];
        const pages = data.resources.results.pages || [];
        const articles = data.resources.results.articles || [];
        const items = [...products, ...pages, ...articles];
        results.innerHTML = items.length ? items.map((item) => `<a href="${item.url}">${item.title}</a>`).join('') : '<p>No quick results.</p>';
      } catch (error) {
        if (error.name !== 'AbortError') results.innerHTML = '<p>Search is unavailable right now.</p>';
      }
    });
  }

  function initFilters() {
    const panel = qs('[data-filter-panel]');
    qsa('[data-filter-toggle]').forEach((button) => button.addEventListener('click', () => panel?.classList.toggle('is-open')));
  }

  function initRecommendations() {
    qsa('product-recommendations').forEach(async (element) => {
      if (element.dataset.ready || !element.dataset.url) return;
      element.dataset.ready = 'true';
      const html = await fetch(element.dataset.url).then((res) => res.text()).catch(() => '');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('product-recommendations');
      if (next && next.innerHTML.trim()) element.innerHTML = next.innerHTML;
    });
  }

  function initShopTabs(root = document) {
    qsa('[data-shop-tabs]', root).forEach((section) => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const tabs = qsa('[data-shop-tab]', section);
      const panels = qsa('[data-shop-panel]', section);
      const activate = (tab) => {
        const key = tab.dataset.shopTab;
        tabs.forEach((button) => {
          const active = button === tab;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', active ? 'true' : 'false');
          button.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.dataset.shopPanel === key;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          tabs[next].focus();
          activate(tabs[next]);
        });
      });
    });
  }

  function init() {
    initHeader();
    initDrawers();
    bindCartControls();
    initProductForms();
    initPredictiveSearch();
    initFilters();
    initRecommendations();
    initShopTabs();
    updateCartCount();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
